import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth";
import type { Invoice } from "@/api.types";

const INVOICE_PAGE_SIZE = 100;

export interface PendingPaymentTermGroup {
  totalOrders: number;
  completedOrders: number;
  totalCents: number;
  completedTotalCents: number;
  oldestOrderDate: string | null;
}

export interface PaymentTermWithPending {
  id: string;
  name: string;
  dayInterval: number;
  paymentDueDays: number;
  pending: PendingPaymentTermGroup | null;
  daysUntilAutoInvoice: number;
}

export interface CreditUsage {
  outstandingBalance: number;
  creditLimit: number;
  utilizationPercent: number;
}

export interface MyPaymentTermsResponse {
  paymentTerms: PaymentTermWithPending[];
  creditLimit: number;
  outstandingBalance: number;
  availableCredit: number;
  creditUsage?: CreditUsage;
  lastInvoiceDate: string | null;
  hasNetTermsAccess: boolean;
  unpaidInvoiceCount?: number;
  hasUnpaidInvoices?: boolean;
}

export function useInvoices() {
  const { customer, token } = useSession();

  return useQuery({
    queryKey: ["invoices", customer?.id],
    queryFn: async () => {
      const invoices: Invoice[] = [];
      let page = 1;
      let totalPages = 1;
      do {
        const response = await axios.get<{
          data: Invoice[];
          metadata?: { totalPages: number };
        }>(`${import.meta.env.VITE_API_URL}/api/v2/customers/me/invoices`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { page, limit: INVOICE_PAGE_SIZE },
        });
        invoices.push(...response.data.data);
        totalPages = response.data.metadata?.totalPages ?? page;
        page += 1;
      } while (page <= totalPages);
      return invoices;
    },
    enabled: !!customer?.id && !!token,
  });
}

export function useMyPaymentTermsWithPending() {
  const { customer, token } = useSession();

  return useQuery({
    queryKey: ["my-payment-terms-pending", customer?.id],
    queryFn: async () => {
      const response = await axios.get<{ data: MyPaymentTermsResponse }>(
        `${import.meta.env.VITE_API_URL}/api/v2/customers/me/payment-terms`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data.data;
    },
    enabled: !!customer?.id && !!token,
  });
}

export function useGenerateInvoice() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (paymentTermsId: string) => {
      // Idempotency-Key generated per logical click. If the request retries
      // (network drop, double-submit), the same key is sent so Stripe dedups
      // and the server returns the already-created invoice.
      const idempotencyKey = crypto.randomUUID();
      const response = await axios.post<{ data: Invoice }>(
        `${import.meta.env.VITE_API_URL}/api/v2/customers/me/invoices/generate`,
        { paymentTermsId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Idempotency-Key": idempotencyKey,
          },
        }
      );
      return response.data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["invoices"] });
      void queryClient.invalidateQueries({
        queryKey: ["my-payment-terms-pending"],
      });
    },
  });
}

export function useUnpaidInvoices() {
  const { data: invoices, ...rest } = useInvoices();

  // Ensure invoices is an array before filtering
  const invoiceArray = Array.isArray(invoices) ? invoices : [];
  const unpaidInvoices = invoiceArray.filter(
    (invoice) =>
      !["PAID", "VOID", "UNCOLLECTIBLE"].includes(invoice.status.toUpperCase())
  );

  return {
    ...rest,
    data: unpaidInvoices,
    count: unpaidInvoices.length,
  };
}
